import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Form, ButtonV2 } from 'components';
import moment from 'moment';
import get from 'lodash/get';
import { isAdmin } from 'lib/utils/selectors';
import { calculateStaffOnly } from './calculateStaffOnly';
import ActivityForms from '../common/ActivityForms';
import buildActivityPayload from '../common/buildActivityPayload';
import EditActivityStudentList from '../CreateActivity/EditActivityStudentList';
import './style.scss';

function EditGroupedActivityForm({
  data,
  onClose,
  staffOnlyActivities,
  historyDate,
  isAdmin,
  constants,
  hideHeader,
  hideFooter
}) {
  const { activity, isFiltered, hideDelete, title, kids } = data;

  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [kidIds, setKidIds] = useState([...activity.kid_ids]);

  const onChange = useCallback(updatedValues => {
    setFormValues(prev => ({
      ...prev,
      ...updatedValues
    }));
  }, []);

  const onUpdateKids = useCallback(ids => {
    setKidIds(ids);
  }, []);

  const submit = useCallback(
    values => {
      if (formValues.title === 'Nap Check' && !formValues.sleep_position) {
        Actions.showFlash('No sleep position selected', 'alert');
        return;
      }

      if (!kidIds.length) {
        Actions.showFlash('No students selected', 'alert');
        return;
      }

      const requestPayload = buildActivityPayload(activity.activity_type, {
        data: {
          ...formValues,
          ...values,
          staffOnly: calculateStaffOnly(isAdmin, staffOnlyActivities.includes(activity.activity_type), values.staffOnly)
        },
        activity,
        isEditing: true
      });

      // Validate date/time constraints
      if (
        moment(activity.activity_time).isBefore(historyDate) &&
        moment(requestPayload.daily_activity.activity_time).isAfter(historyDate)
      ) {
        Actions.showFlash(`Activity time should be before ${moment(historyDate).format('YYYY-MM-DD')}`, 'alert');
        return;
      }

      if (
        moment(activity.activity_time).isAfter(historyDate) &&
        moment(requestPayload.daily_activity.activity_time).isBefore(historyDate)
      ) {
        Actions.showFlash(`Activity time should be after ${moment(historyDate).format('YYYY-MM-DD')}`, 'alert');
        return;
      }

      setIsLoading(true);

      requestPayload.id = activity.batch_id;
      requestPayload.daily_activity.kid_ids = kidIds;
      if (isFiltered) {
        requestPayload.kid_ids = activity.kid_ids;
      }

      return req
        .updateBatchDailyActivity(requestPayload, undefined, {
          checkHistory: activity.activity_time
        })
        .then(resp => {
          setIsLoading(false);
          onClose(resp.daily_activities[0]);
        })
        .catch(err => {
          setIsLoading(false);
          Actions.reportError('There was problem updating activity', err);
        });
    },
    [formValues, kidIds, activity, staffOnlyActivities, isAdmin, historyDate, onClose, isFiltered]
  );

  const EditForm = ActivityForms[activity.activity_type];
  const signatures = activity.activiable?.signatures;
  const forceStaff = !isAdmin && (staffOnlyActivities.includes(activity.activity_type) || activity.is_staff_only);

  return (
    <div className="edit-activity">
      {!hideHeader && (
        <div className="modal__header">
          <span className="modal__header-title">Edit {title} Activity</span>
        </div>
      )}

      <Form
        className="edit-activity__form"
        onSubmit={submit}
        onChange={onChange}
        validateOn="submit"
        isLoading={isLoading}
      >
        <div className="edit-activity__form-container">
          <EditForm
            isEditing
            onChange={onChange}
            activity={activity}
            signatures={signatures}
            formValues={formValues}
            forceStaff={forceStaff}
            constants={constants}
          />
          <EditActivityStudentList
            value={kidIds}
            onChange={onUpdateKids}
            kids={kids}
            hideSelect={isFiltered}
            hideDelete={hideDelete}
          />
        </div>

        {!hideFooter && (
          <div className="form__row--actions">
            <ButtonV2 secondary onClick={() => onClose(null)} label="Cancel" />
            <Form.Submit label="Save" data-cy="save" />
          </div>
        )}
      </Form>
    </div>
  );
}

const mapStateToProps = state => ({
  isAdmin: isAdmin(state),
  staffOnlyActivities: state.currentUser.data.current_school.staff_only_activities,
  constants: state.constants.data.daily_activities,
  historyDate: get(state, 'constants.data.history_dates.daily_activities'),
  kids: state.students.data
});

export default connect(mapStateToProps)(EditGroupedActivityForm);
